import { isFailedCheck } from "./resultQuality";

const RESPIRATORY_HINT_CHECKS = [
  {
    key: "movement",
    checks: [
      "drift",
      "driftcheck",
      "baseline_drift",
      "baselinedrift",
      "jump",
      "jump_artifact",
      "jumpartifact",
      "motion",
      "movement",
    ],
  },
  {
    key: "framing",
    checks: [
      "nan",
      "nancheck",
      "constant_signal",
      "constantsignal",
      "no_tidal_data",
      "notidaldata",
    ],
  },
  {
    key: "breathing",
    checks: [
      "low_amplitude",
      "lowamplitude",
      "few_peaks",
      "fewpeaks",
      "peak_count",
      "peakcount",
    ],
  },
];

const DEFAULT_HINTS = {
  movement:
    "A sudden movement affected the recording. Keep your hands, chest and face still until it finishes.",
  framing:
    "Keep your face, shoulders, chest and upper abdomen clearly visible in the camera frame.",
  breathing:
    "Sit comfortably and breathe normally and steadily until the recording finishes.",
  recording_setup:
    "Stay still, remain in the camera frame and follow the on-screen instructions until the recording finishes.",
  heartdx_lighting:
    "Improve the lighting on your face. Use even light from in front of you and avoid a bright window or light behind you.",
  heartdx_still:
    "Keep your face still and look toward the camera until the recording finishes.",
};

function hasFailedCheck(checkCollections, checkNames) {
  const allowedNames = new Set(checkNames);

  return checkCollections.some((checks) => {
    if (!checks || typeof checks !== "object" || Array.isArray(checks)) {
      return false;
    }

    return Object.entries(checks).some(([name, value]) => {
      const normalizedName = String(name).trim().toLowerCase();
      return allowedNames.has(normalizedName) && isFailedCheck(value);
    });
  });
}

export function getRespiratoryQualityHintKeys(checks) {
  const checkCollections = Array.isArray(checks) ? checks : [checks];

  return RESPIRATORY_HINT_CHECKS.filter(({ checks: checkNames }) =>
    hasFailedCheck(checkCollections, checkNames),
  ).map(({ key }) => key);
}

/**
 * Builds patient-facing, non-diagnostic repeat advice from quality failures.
 * HeartDX currently provides an overall quality outcome rather than individual
 * failure causes, so its advice is deliberately framed as setup guidance.
 */
export function getRecordingQualityFeedbackHints(
  t,
  {
    respiratoryFailed = false,
    respiratoryChecks = null,
    cardiacFailed = false,
  } = {},
) {
  const hintKeys = [];

  if (respiratoryFailed) {
    const respiratoryHintKeys = getRespiratoryQualityHintKeys(
      respiratoryChecks,
    );
    hintKeys.push(
      ...(respiratoryHintKeys.length
        ? respiratoryHintKeys
        : ["recording_setup"]),
    );
  }

  if (cardiacFailed) {
    hintKeys.push("heartdx_lighting", "heartdx_still");
  }

  return [...new Set(hintKeys)].map((key) =>
    t(`result.cardiopulmonary.qualityHints.items.${key}`, DEFAULT_HINTS[key]),
  );
}
